// src/context/SocketContext.js

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from './AuthContext';
import { SocketHandler } from '../services/socketService';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { token } = useContext(AuthContext);
    const [isConnected, setIsConnected] = useState(false);
    const [socket, setSocket] = useState(null);

    // Maintain the SocketHandler instance in a ref so it doesn't get recreated on re-renders
    const socketHandlerRef = useRef(null);
    const connectionStateUnsubscribeRef = useRef(null);

    // Initialize the handler once
    if (!socketHandlerRef.current) {
        socketHandlerRef.current = new SocketHandler();
    }

    const socketHandler = socketHandlerRef.current;

    // Handle token changes (login/logout)
    useEffect(() => {
        if (token) {
            console.log("[SocketProvider] Token detected (login), connecting socket...");
            socketHandler.connect(token);

            // Get the singleton socket instance
            const socketInstance = socketHandler.getSocket();
            setSocket(socketInstance);

            if (socketInstance) {
                // Set initial connection state
                setIsConnected(socketInstance.connected);

                // Register connection state callback from SocketHandler
                const unsubscribe = socketHandler.onConnectionStateChange((connected) => {
                    console.log("[SocketProvider] Connection state changed:", connected);
                    setIsConnected(connected);
                });

                connectionStateUnsubscribeRef.current = unsubscribe;

                return () => {
                    if (connectionStateUnsubscribeRef.current) {
                        connectionStateUnsubscribeRef.current();
                        connectionStateUnsubscribeRef.current = null;
                    }
                };
            }
        } else {
            // Token removed (logout)
            console.log("[SocketProvider] Token removed or missing, disconnecting socket...");
            socketHandler.disconnect();
            setIsConnected(false);
            setSocket(null);

            if (connectionStateUnsubscribeRef.current) {
                connectionStateUnsubscribeRef.current();
                connectionStateUnsubscribeRef.current = null;
            }
        }
    }, [token, socketHandler]);

    // Value to expose to consumers
    const value = {
        socketService: socketHandler,
        isConnected,
        socket: socket
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketProvider;